import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  BaseMenuRequest,
  BaseMenuCategoryRequest,
  BaseMenuItemRequest,
  CreateBaseMenuRequest,
  CreateBaseMenuCategoryRequest,
  CreateBaseMenuItemRequest,
  BaseMenuDeleteRequest,
  DeleteBaseMenuCategoryRequest,
  DeleteBaseMenuItemRequest,
  BaseMenuListResponse,
  BaseMenuCategoryListResponse,
  BaseMenuItemListResponse,
  BaseMenuResponse,
  BaseMenuDeleteResponse,
  CreateMenuCustomFieldRequest,
  CreateMenuCustomFieldResponse,
  GetMenuCustomFieldsRequest,
  GetMenuCustomFieldsResponse,
  DeleteMenuCustomFieldRequest,
  DeleteMenuCustomFieldResponse,
} from "../../models/BaseMenu.model";
import { environment } from "../../environments";

const API_BASE_URL = environment.apiUrl;

// קבלת רשימת תפריטי בסיס
export const getBaseMenus = createAsyncThunk<
  BaseMenuListResponse,
  BaseMenuRequest,
  { rejectValue: string }
>("baseMenu/getMenus", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}BaseMenu/GetBaseMenus`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: BaseMenuListResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to fetch base menus");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error"
    );
  }
});

// יצירת/עדכון תפריט בסיס
export const createOrUpdateBaseMenu = createAsyncThunk<
  BaseMenuResponse,
  CreateBaseMenuRequest,
  { rejectValue: string }
>("baseMenu/createOrUpdateMenu", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}BaseMenu/CreateOrUpdateBaseMenu`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      }
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: BaseMenuResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to create/update menu");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error"
    );
  }
});

// מחיקת תפריט בסיס
export const deleteBaseMenu = createAsyncThunk<
  BaseMenuDeleteResponse,
  BaseMenuDeleteRequest,
  { rejectValue: string }
>("baseMenu/deleteMenu", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}BaseMenu/DeleteBaseMenu`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: BaseMenuDeleteResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to delete menu");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error"
    );
  }
});

// קבלת קטגוריות תפריט
export const getBaseMenuCategories = createAsyncThunk<
  BaseMenuCategoryListResponse,
  BaseMenuCategoryRequest,
  { rejectValue: string }
>("baseMenu/getCategories", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}BaseMenu/GetBaseMenuCategories`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      }
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: BaseMenuCategoryListResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to fetch menu categories");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error"
    );
  }
});

// יצירת/עדכון קטגוריית תפריט
export const createOrUpdateBaseMenuCategory = createAsyncThunk<
  BaseMenuResponse,
  CreateBaseMenuCategoryRequest,
  { rejectValue: string }
>(
  "baseMenu/createOrUpdateCategory",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}BaseMenu/CreateOrUpdateBaseMenuCategory`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: BaseMenuResponse = await response.json();

      if (!data.success)
        return rejectWithValue(
          data.message || "Failed to create/update category"
        );
      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// מחיקת קטגוריית תפריט
export const deleteBaseMenuCategory = createAsyncThunk<
  BaseMenuDeleteResponse,
  DeleteBaseMenuCategoryRequest,
  { rejectValue: string }
>("baseMenu/deleteCategory", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}BaseMenu/DeleteBaseMenuCategory`,
      {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      }
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: BaseMenuDeleteResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to delete category");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error"
    );
  }
});

// קבלת פריטי תפריט
export const getBaseMenuItems = createAsyncThunk<
  BaseMenuItemListResponse,
  BaseMenuItemRequest,
  { rejectValue: string }
>("baseMenu/getItems", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}BaseMenu/GetBaseMenuItems`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: BaseMenuItemListResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to fetch menu items");

    // המר את ה-tags וה-allergens מ-string ל-array
    const processedData = {
      ...data,
      data: data.data.map((item) => ({
        ...item,
        baseMenuItem_Tags: item.baseMenuItem_Tags
          ? typeof item.baseMenuItem_Tags === "string"
            ? JSON.parse(item.baseMenuItem_Tags)
            : item.baseMenuItem_Tags
          : [],
        // ✅ המר גם את האלרגנים מ-string ל-array
        baseMenuItem_AllergansJSON: item.baseMenuItem_AllergansJSON
          ? typeof item.baseMenuItem_AllergansJSON === "string"
            ? item.baseMenuItem_AllergansJSON
            : item.baseMenuItem_AllergansJSON
          : "[]",
      })),
    };

    return processedData;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error"
    );
  }
});

// עדכן את createOrUpdateBaseMenuItem
export const createOrUpdateBaseMenuItem = createAsyncThunk<
  BaseMenuResponse,
  CreateBaseMenuItemRequest,
  { rejectValue: string }
>("baseMenu/createOrUpdateItem", async (requestData, { rejectWithValue }) => {
  try {
    // המר את המערך ל-JSON string לפני השליחה לשרת
    const serverPayload = {
      token: requestData.token,
      companyKey: requestData.companyKey,
      menuCategoryID: requestData.menuCategoryID,
      menuItemID: requestData.menuItemID,
      itemName: requestData.itemName,
      itemDescription: requestData.itemDescription,
      itemImageURL: requestData.itemImageURL,
      itemAllergansJSON: requestData.itemAllergansJSON, // כבר JSON string
      itemIsSeasonality: requestData.itemIsSeasonality,
      itemSpicinessLevel: requestData.itemSpicinessLevel,
      itemIsChildrenFriendly: requestData.itemIsChildrenFriendly,
      itemIsExtraCost: requestData.itemIsExtraCost,
      itemExtraCostPerHead: requestData.itemExtraCostPerHead,
      itemFoodCost: requestData.itemFoodCost,
      itemTags: JSON.stringify(requestData.itemTags || []),
      qtyPercent: Number(requestData.qtyPercent) || 0,
      // המרה ל-JSON string
    };

    const response = await fetch(
      `${API_BASE_URL}BaseMenu/CreateOrUpdateBaseMenuItem`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(serverPayload),
      }
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: BaseMenuResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to create/update item");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error"
    );
  }
});

// מחיקת פריט תפריט
export const deleteBaseMenuItem = createAsyncThunk<
  BaseMenuDeleteResponse,
  DeleteBaseMenuItemRequest,
  { rejectValue: string }
>("baseMenu/deleteItem", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}BaseMenu/DeleteBaseMenuItem`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: BaseMenuDeleteResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to delete item");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error"
    );
  }
});

// יצירת/עדכון שדה מותאם אישית
export const createMenuCustomField = createAsyncThunk<
  CreateMenuCustomFieldResponse,
  CreateMenuCustomFieldRequest,
  { rejectValue: string }
>("baseMenu/createCustomField", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}BaseMenu/CreateMenuCustomField`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      }
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: CreateMenuCustomFieldResponse = await response.json();

    if (!data.success)
      return rejectWithValue(
        data.message || "Failed to create/update custom field"
      );
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error"
    );
  }
});

// קבלת שדות מותאמים אישית
export const getMenuCustomFields = createAsyncThunk<
  GetMenuCustomFieldsResponse,
  GetMenuCustomFieldsRequest,
  { rejectValue: string }
>("baseMenu/getCustomFields", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}BaseMenu/GetMenuCustomFields`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      }
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: GetMenuCustomFieldsResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to fetch custom fields");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error"
    );
  }
});

// מחיקת שדה מותאם אישית
export const deleteMenuCustomField = createAsyncThunk<
  DeleteMenuCustomFieldResponse,
  DeleteMenuCustomFieldRequest,
  { rejectValue: string }
>("baseMenu/deleteCustomField", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}BaseMenu/DeleteMenuCustomField`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      }
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: DeleteMenuCustomFieldResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to delete custom field");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error"
    );
  }
});
